/**
 * Comment actions of the reader (add, reply, edit, resolve, delete, unwrap
 * marks) — moved verbatim out of App.tsx; App passes the state they use.
 */
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from "react";
import { useCallback, useEffect } from "react";
import { type OpenedFile } from "../lib/file";
import { loadLocalComments, type PdfCommentAnchor, type ReaderComment } from "../lib/annotationStore";

export type CommentActionDeps = {
  file: OpenedFile | null;
  docxHtml: string;
  readerComments: ReaderComment[];
  activeCommentId: string | null;
  setActiveCommentId: Dispatch<SetStateAction<string | null>>;
  focusCommentId: string | null;
  setFocusCommentId: Dispatch<SetStateAction<string | null>>;
  addCommentQuote: string | null;
  setAddCommentQuote: Dispatch<SetStateAction<string | null>>;
  pendingPdfAnchorRef: MutableRefObject<PdfCommentAnchor | null>;
  docxContainerRef: RefObject<HTMLDivElement | null>;
  pdfContainerRef: RefObject<HTMLDivElement | null>;
  savedRangeRef: MutableRefObject<Range | null>;
  hideSelection: () => void;
  persistComments: (next: ReaderComment[]) => void;
};

export function useCommentActions({
  file, docxHtml, readerComments, activeCommentId, setActiveCommentId, focusCommentId, setFocusCommentId, addCommentQuote, setAddCommentQuote, pendingPdfAnchorRef, docxContainerRef, pdfContainerRef, savedRangeRef, hideSelection, persistComments,
}: CommentActionDeps) {
  const submitAddComment = useCallback(
    (body: string) => {
      const quote = addCommentQuote ?? "";
      const id = `local-${crypto.randomUUID().slice(0, 8)}`;
      const pdfAnchor = pendingPdfAnchorRef.current ?? undefined;
      pendingPdfAnchorRef.current = null;
      const comment: ReaderComment = {
        word_id: id,
        author: "من",
        date: new Date().toISOString(),
        body,
        quote,
        replies: [],
        resolved: false,
        pdfAnchor,
      };
      const next = [comment, ...readerComments];
      persistComments(next);

      const range = savedRangeRef.current;
      if (range) {
        try {
          const mark = document.createElement("mark");
          mark.className = "nr-comment-range";
          mark.dataset.commentIds = id;
          range.surroundContents(mark);
        } catch {
          /* complex selection */
        }
      }
      savedRangeRef.current = null;
      setAddCommentQuote(null);
      setFocusCommentId(id);
      setActiveCommentId(id);
      hideSelection();
    },
    [addCommentQuote, hideSelection, persistComments, readerComments]
  );

  const replyToComment = useCallback(
    (id: string, body: string) => {
      const next = readerComments.map((c) =>
        c.word_id === id
          ? {
              ...c,
              replies: [
                ...(c.replies ?? []),
                {
                  id: crypto.randomUUID(),
                  author: "من",
                  date: new Date().toISOString(),
                  body,
                },
              ],
            }
          : c
      );
      persistComments(next);
    },
    [persistComments, readerComments]
  );

  const unwrapCommentMarks = useCallback((ids: Set<string> | string[]) => {
    const remove = ids instanceof Set ? ids : new Set(ids);
    if (remove.size === 0) return;
    const roots = [docxContainerRef.current, pdfContainerRef.current].filter(
      Boolean
    ) as HTMLElement[];
    if (roots.length === 0) return;

    for (const root of roots) {
      const marks = Array.from(
        root.querySelectorAll<HTMLElement>(
          ".nr-comment-range, .nr-comment-anchor"
        )
      );
      for (const el of marks) {
        const raw =
          el.getAttribute("data-comment-ids") ||
          el.getAttribute("data-comment-id") ||
          "";
        const list = raw.trim().split(/\s+/).filter(Boolean);
        if (!list.some((id) => remove.has(id))) continue;
        const remaining = list.filter((id) => !remove.has(id));
        if (
          remaining.length === 0 ||
          el.classList.contains("nr-comment-anchor")
        ) {
          if (
            el.classList.contains("nr-comment-range") ||
            el.tagName === "MARK"
          ) {
            const parent = el.parentNode;
            if (!parent) continue;
            while (el.firstChild) parent.insertBefore(el.firstChild, el);
            parent.removeChild(el);
            parent.normalize?.();
          } else {
            el.remove();
          }
        } else {
          el.setAttribute("data-comment-ids", remaining.join(" "));
        }
      }
    }
  }, []);

  // After DOCX HTML inject, remove marks for soft-deleted comments from storage
  useEffect(() => {
    if (!file || file.kind !== "docx" || !docxHtml) return;
    const deleted = loadLocalComments(file.name)
      .filter((c) => c.deleted)
      .map((c) => c.word_id);
    if (deleted.length === 0) return;
    const t = window.setTimeout(() => unwrapCommentMarks(deleted), 0);
    return () => window.clearTimeout(t);
  }, [docxHtml, file, unwrapCommentMarks]);

  const deleteComment = useCallback(
    (id: string) => {
      const previouslyVisible = readerComments.filter((c) => !c.deleted);
      const idx = previouslyVisible.findIndex((c) => c.word_id === id);
      const next = readerComments.map((c) =>
        c.word_id === id ? { ...c, deleted: true } : c
      );
      persistComments(next);
      unwrapCommentMarks([id]);

      const remaining = next.filter((c) => !c.deleted);
      if (remaining.length === 0) {
        setFocusCommentId(null);
        setActiveCommentId(null);
        return;
      }

      // Keep sheet open on a neighbor when the focused root was removed
      if (focusCommentId === id || activeCommentId === id) {
        const neighbor =
          remaining[Math.min(Math.max(idx, 0), remaining.length - 1)] ??
          remaining[0]!;
        setFocusCommentId(neighbor.word_id);
        setActiveCommentId(neighbor.word_id);
      }
    },
    [
      activeCommentId,
      focusCommentId,
      persistComments,
      readerComments,
      unwrapCommentMarks,
    ]
  );

  const deleteReply = useCallback(
    (commentId: string, replyId: string) => {
      const next = readerComments.map((c) =>
        c.word_id === commentId
          ? {
              ...c,
              replies: (c.replies ?? []).filter((r) => r.id !== replyId),
            }
          : c
      );
      persistComments(next);
    },
    [persistComments, readerComments]
  );

  const resolveComment = useCallback(
    (id: string) => {
      const next = readerComments.map((c) =>
        c.word_id === id ? { ...c, resolved: true } : c
      );
      persistComments(next);
    },
    [persistComments, readerComments]
  );

  const resolveAllComments = useCallback(() => {
    const next = readerComments.map((c) =>
      c.deleted ? c : { ...c, resolved: true }
    );
    persistComments(next);
  }, [persistComments, readerComments]);

  const deleteAllComments = useCallback(() => {
    const ids = readerComments.filter((c) => !c.deleted).map((c) => c.word_id);
    const next = readerComments.map((c) => ({ ...c, deleted: true }));
    persistComments(next);
    unwrapCommentMarks(ids);
    setFocusCommentId(null);
    setActiveCommentId(null);
  }, [persistComments, readerComments, unwrapCommentMarks]);

  const editComment = useCallback(
    (id: string, body: string) => {
      const next = readerComments.map((c) =>
        c.word_id === id ? { ...c, body } : c
      );
      persistComments(next);
    },
    [persistComments, readerComments]
  );

  const editReply = useCallback(
    (commentId: string, replyId: string, body: string) => {
      const next = readerComments.map((c) =>
        c.word_id === commentId
          ? {
              ...c,
              replies: (c.replies ?? []).map((r) =>
                r.id === replyId ? { ...r, body } : r
              ),
            }
          : c
      );
      persistComments(next);
    },
    [persistComments, readerComments]
  );

  return {
    submitAddComment, replyToComment, unwrapCommentMarks, deleteComment, deleteReply, resolveComment, resolveAllComments, deleteAllComments, editComment, editReply,
  };
}
